import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { Trash2, MailCheck, MailOpen, Archive, LogOut, Search } from "lucide-react";
import { fetchLeads, fetchStats, updateLeadStatus, deleteLead, setToken, getToken, adminMe } from "../lib/api";

const STATUS_TABS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "read", label: "Read" },
  { key: "archived", label: "Archived" },
];

function formatDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { nav("/admin/login"); return; }
    adminMe().catch(() => { setToken(null); nav("/admin/login"); });
    refresh();
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(), fetchStats()]);
      setLeads(l); setStats(s);
    } catch (err) {
      if (err?.response?.status === 401) { setToken(null); nav("/admin/login"); return; }
      toast.error("Failed to load leads.");
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    return leads
      .filter((l) => tab === "all" ? true : l.status === tab)
      .filter((l) => {
        if (!q) return true;
        const t = q.toLowerCase();
        return [l.name, l.email, l.company, l.message].filter(Boolean).some((x) => x.toLowerCase().includes(t));
      });
  }, [leads, tab, q]);

  const setStatus = async (id, status) => {
    try { await updateLeadStatus(id, status); toast.success(`Marked as ${status}.`); refresh(); }
    catch { toast.error("Update failed."); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try { await deleteLead(id); toast.success("Deleted."); setSelected(null); refresh(); }
    catch { toast.error("Delete failed."); }
  };
  const logout = () => { setToken(null); nav("/admin/login"); };

  return (
    <div className="min-h-[100svh] bg-cream text-ink" data-testid="admin-dashboard-page">
      <Toaster richColors position="top-right" />
      <header className="border-b border-ink/15 px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <span className="font-display text-3xl uppercase">M Media · Atelier</span>
          <span className="overline text-ink/50 hidden md:inline">Internal Lead Intake</span>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="overline text-ink/60 hover:text-ink">View Site →</a>
          <button onClick={logout} className="btn-ghost" data-testid="admin-logout"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-5 border-b border-ink/15">
          {[
            { l: "Total", v: stats.total },
            { l: "New", v: stats.new },
            { l: "Read", v: stats.read },
            { l: "Archived", v: stats.archived },
            { l: "Subscribers", v: stats.subscribers },
          ].map((s, i) => (
            <div key={s.l} className={`p-6 md:p-8 ${i !== 0 ? "border-l border-ink/15" : ""}`}>
              <div className="overline text-ink/50">{s.l}</div>
              <div className="font-display text-5xl md:text-6xl mt-1">{s.v}</div>
            </div>
          ))}
        </section>
      )}

      <section className="px-6 md:px-10 py-6 flex flex-wrap gap-3 items-center justify-between border-b border-ink/15">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`overline px-4 py-2 border transition ${tab === t.key ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 border-b border-ink/30 px-2">
          <Search size={14} className="text-ink/55" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…" className="bg-transparent py-2 outline-none w-64" data-testid="admin-search" />
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12">
        <div className="lg:col-span-7 border-r border-ink/15 min-h-[60svh]">
          {loading ? (
            <div className="p-10 overline text-ink/50">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 overline text-ink/50" data-testid="admin-empty">No leads in this view.</div>
          ) : (
            <ul>
              {filtered.map((l) => (
                <li key={l.id}
                  onClick={() => setSelected(l)}
                  className={`px-6 md:px-10 py-5 border-b border-ink/10 cursor-pointer hover:bg-ink/5 ${selected?.id === l.id ? "bg-ink/5" : ""}`}
                  data-testid={`admin-lead-${l.id}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <div>
                      <div className="font-display uppercase text-2xl">{l.name}</div>
                      <div className="overline text-ink/55 mt-1">{l.email}{l.company ? ` · ${l.company}` : ""}</div>
                    </div>
                    <div className="text-right">
                      <span className={`overline px-2 py-1 border ${l.status === "new" ? "border-ink text-ink" : l.status === "read" ? "border-ink/40 text-ink/60" : "border-ink/20 text-ink/40"}`}>
                        {l.status}
                      </span>
                      <div className="overline text-ink/40 mt-2">{formatDate(l.created_at)}</div>
                    </div>
                  </div>
                  <p className="text-ink/70 mt-3 line-clamp-2">{l.message}</p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <aside className="lg:col-span-5 p-6 md:p-10 lg:sticky lg:top-0 lg:h-[calc(100svh-65px)] overflow-y-auto">
          {selected ? (
            <div data-testid="admin-detail">
              <div className="overline text-ink/55">Lead detail</div>
              <h2 className="font-display uppercase text-5xl leading-[0.9] mt-3">{selected.name}</h2>
              <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                <div><div className="overline text-ink/50">Email</div><a className="underline" href={`mailto:${selected.email}`}>{selected.email}</a></div>
                {selected.phone && <div><div className="overline text-ink/50">Phone</div><a className="underline" href={`tel:${selected.phone}`}>{selected.phone}</a></div>}
                {selected.title && <div><div className="overline text-ink/50">Title</div><span>{selected.title}</span></div>}
                {selected.company && <div><div className="overline text-ink/50">Company</div><span>{selected.company}</span></div>}
                {selected.website && <div className="col-span-2"><div className="overline text-ink/50">Website</div><a className="underline break-all" href={selected.website} target="_blank" rel="noreferrer">{selected.website}</a></div>}
                {selected.stage && <div><div className="overline text-ink/50">Stage</div><span>{selected.stage}</span></div>}
                {selected.revenue && <div><div className="overline text-ink/50">Revenue</div><span>{selected.revenue}</span></div>}
                {selected.budget && <div><div className="overline text-ink/50">Budget</div><span>{selected.budget}</span></div>}
                {selected.referrer && <div><div className="overline text-ink/50">Found us via</div><span>{selected.referrer}</span></div>}
                <div className="col-span-2"><div className="overline text-ink/50">Submitted</div><span>{formatDate(selected.created_at)}</span></div>
              </div>

              {selected.services?.length > 0 && (
                <div className="mt-6">
                  <div className="overline text-ink/50 mb-2">Divisions of interest</div>
                  <div className="flex flex-wrap gap-2">
                    {selected.services.map((s) => (
                      <span key={s} className="overline px-3 py-1 border border-ink/30">{s}</span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="overline text-ink/50 mb-2">Message</div>
                <p className="font-serif text-xl leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>

              {selected.achievements && (
                <div className="mt-6">
                  <div className="overline text-ink/50 mb-2">Achievements / Press</div>
                  <p className="text-ink/80 leading-relaxed whitespace-pre-wrap">{selected.achievements}</p>
                </div>
              )}

              {selected.why_us && (
                <div className="mt-6">
                  <div className="overline text-ink/50 mb-2">Why M Media</div>
                  <p className="text-ink/80 leading-relaxed whitespace-pre-wrap">{selected.why_us}</p>
                </div>
              )}

              <div className="mt-10 flex flex-wrap gap-3">
                <button onClick={() => setStatus(selected.id, "read")} className="btn-ghost" data-testid="admin-mark-read"><MailOpen size={14} /> Mark read</button>
                <button onClick={() => setStatus(selected.id, "new")} className="btn-ghost" data-testid="admin-mark-new"><MailCheck size={14} /> Mark new</button>
                <button onClick={() => setStatus(selected.id, "archived")} className="btn-ghost" data-testid="admin-archive"><Archive size={14} /> Archive</button>
                <button onClick={() => remove(selected.id)} className="btn-ink" data-testid="admin-delete"><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ) : (
            <div className="overline text-ink/50">Select a lead to view details.</div>
          )}
        </aside>
      </section>
    </div>
  );
}
